import React from 'react';
import { formatCurrency, generateId, statusColors, translate } from '../../utility/functions';
import Table from '../../table/Table';
import Button from '../../forms/Button';
import GetState from '../../GetState';
import { QuestRoute } from '../../api/QuestRoutes';
import Form from '../../forms/Form';
import HamQuest from '../../api/HamQuestGiver';
import Input from '../../forms/Input';
import Preput from '../../forms/Preput';
import Select from '../../forms/Select';
import { HSOptions } from '../../../constants/HSOptions';
import Checkbox from '../../forms/Checkbox';
import TerminalIDsSelector from '../../forms/formComponents/TerminalIDsSelector';
import LocationSelectors from '../../forms/formComponents/LocationSelectors';
import ImageIndex from '../../forms/formComponents/ImageIndex';
import ColorInput from '../../forms/formComponents/ColorInput';
import Icon from '../../forms/Icon';
import { userRoles } from '../../RoleProtected';
import ManagementRow from '../../table/ManagementRow';
import ModalFactory from '../../Modal';

const Products = ({ ...props }) => {
    const [kioskItems] = GetState(QuestRoute.kiosks.wash, { loading: [] });
    const [products, refreshProducts] = GetState(QuestRoute.products.all);

    const deleteProduct = (product) => {
        ModalFactory.confirmAndProcess({
            confirmation: {
                title: `Are you sure?`,
                body: `Are you sure you want to delete ${product.name}?`,
                type: 'warning',
            },
            promise: {
                title: 'Deleting Product',
                body: `Deleting ${product.name}`,
                callback: () => HamQuest.delete(QuestRoute.products.edit(product.appProductId))
            },
            confirmed: {
                title: 'Success',
                body: `${product.name} successfully deleted.`,
                type: 'success'
            },
            finally: () => refreshProducts()
        })
    }

    const MobileProducts = ({ values, ...props }) => {
        return (
            <>
                <Checkbox
                    label='Active'
                    name='active'
                />
                <Select
                    label='Product Type'
                    name='productType'
                    options={HSOptions.productType}
                    required
                />

                <Select
                    label='Kiosk Item'
                    name='kioskItemId'
                    options={kioskItems.map(e => ({ label: e.name, value: e.id }))}
                    required
                />
                <Checkbox
                    label='Use Kiosk Item Name'
                    name='useKioskItemName'
                />
                <Input
                    label='Name'
                    name='name'
                    disable={values.useKioskItemName}
                    required={!values.useKioskItemName}
                />
                <Checkbox
                    label='Use Kiosk Item Details'
                    name='useKioskItemDetails'
                />
                <Preput
                    label='Details'
                    name='details'
                    disabled={values.useKioskItemDetails}
                />

                <Input
                    label='Quantity'
                    name='quantity'
                    type='number'
                    disable={values.productType !== 'AppBundle'}
                    min={1}
                />
                <Checkbox
                    label='Use Kiosk Price'
                    name='useKioskItemPrice'
                    disabled={values.productType === 'AppBundle'}
                />

                <Input
                    label='Price'
                    name='price'
                    type='price'
                    disable={values.useKioskItemPrice && values.productType !== 'AppBundle'}
                    required={!values.useKioskItemPrice && values.productType !== 'AppBundle'}
                />
                <Checkbox
                    label='Is taxed'
                    name='taxable'
                />
                {values.productType === 'AppBundle' && (
                    <Input
                        label='Limit Start Date'
                        name='purchaseLimitStartDate'
                        type='date'
                        disabled={values.productType !== 'AppBundle'}
                    />
                )}

                <Input
                    label='Purchase Limit'
                    name='purchaseLimit'
                    type='number'
                    min={0}
                />

                <ImageIndex />

                <ColorInput />

                <LocationSelectors />

                <TerminalIDsSelector />
            </>
        )
    }

    const addProduct = (e) => {
        const id = generateId();
        ModalFactory.show({
            id,
            title: `Add new Product`,
            body: (
                <Form
                    name='mobileProductSetting'
                    inputs={{
                        active: true,
                        productType: '',
                        kioskItemId: '',
                        useKioskItemName: false,
                        useKioskItemDetails: false,
                        details: '',
                        quantity: '',
                        useKioskItemPrice: false,
                        taxable: false,
                        price: '',
                        locations: [],
                        terminalIds: [],
                        logoColor: '',
                        logoImageIndex: 0,
                        purchaseLimit: '',
                        purchaseLimitStartDate: '',
                    }}
                    onSubmit={e => HamQuest.post(QuestRoute.products.all, { ...e })
                        .then(() => {
                            refreshProducts();
                            ModalFactory.close(id);
                        })
                    }
                    text='Add Product'
                >
                    {({ values }) => <MobileProducts values={values} />}
                </Form>
            )
        })
    }

    const editProduct = (e) => {
        // console.log(e)
        const id = generateId();
        ModalFactory.show({
            id,
            title: `Edit ${e.name}`,
            body: (
                <Form
                    name='mobileProductSetting'
                    inputs={{
                        ...e
                    }}
                    onSubmit={e => HamQuest.put(QuestRoute.products.edit(e.appProductId), { ...e })
                        .then(() => {
                            refreshProducts();
                            ModalFactory.close(id);
                        })
                    }
                    text='Update'
                >
                    {({ values }) => <MobileProducts values={values} />}
                </Form>
            )
        })
    }

    return (
        <Table
            config={{
                controls: [<Button roles={userRoles.editProducts} onClick={addProduct}>Add Product</Button>],
                data: products,
                headers: [
                    {
                        label: 'Product Name',
                        propKey: 'name',
                    }, {
                        label: 'Product Type',
                        propKey: 'productType',
                        options: true,
                    }, {
                        label: 'Kiosk Item',
                        propKey: 'kioskItem',
                        options: kioskItems.map(e => e.name),
                    }, {
                        label: 'Quantity',
                        propKey: 'quantity',
                        type: 'number'
                    }, {
                        label: 'Price',
                        propKey: 'price',
                        type: 'number'
                    }, {
                        label: 'Active',
                        propKey: 'active',
                        options: true
                    },
                ],
                quickSort: [
                    {
                        label: 'Active Products',
                        propKey: 'active',
                        query: 'true',
                        color: '#21D59B',
                        defaultSearch: true
                    },
                ],
                renderFormat: (e) => (
                    <ManagementRow
                        key={generateId()}
                        controls={[
                            <Button roles={userRoles.editProducts} key={generateId()} onClick={() => editProduct(e)}>Edit</Button>,
                            <Button roles={userRoles.editProducts} key={generateId()} onClick={() => deleteProduct(e)}>Delete</Button>
                        ]}
                    >
                        <td>{e.name}</td>
                        <td>{translate(e.productType)}</td>
                        <td>{kioskItems.find(f => f.id === e.kioskItemId)?.name || e.kioskItemId}</td>
                        <td>{e.quantity}</td>
                        <td>{formatCurrency(e.price)}</td>
                        <td><Icon icon={e.active ? 'check' : 'cross'} style={{ color: e.active ? statusColors.Active : statusColors.NotValidated }} /></td>
                    </ManagementRow>
                )
            }}
        />
    );
}

export default Products;